import { useMemo } from "react";
import { useQueryTemplateData } from "@/hooks/useQueryTemplateData";
import type { Component } from "@/types";
import {
  parseComponentDataBinding,
  type ComponentDataWriteOperation,
} from "@/types/componentDataBinding";
import {
  createLegacyQueryTemplateDataSource,
  mapCatalogSourceErrorToResolverState,
  resolveComponentData,
} from "./componentDataResolver";
import type {
  ComponentDataResolverOptions,
  ComponentDataResolverResult,
  ComponentDataResolverRuntimeState,
  ComponentDataResolverWriteTargetRuntimeState,
} from "./componentDataResolver";
import {
  useSchemaBindingData,
  useSchemaBindingSourceResolution,
} from "./useSchemaBindingData";
import { useComponentDataViewRuntime } from "./useComponentDataViewRuntime";

export interface UseComponentDataResolverOptions
  extends Omit<ComponentDataResolverOptions, "legacyQueryTemplateState"> {
  enabled?: boolean;
}

export function useComponentDataResolver(
  component: Component,
  options: UseComponentDataResolverOptions = {},
): ComponentDataResolverResult {
  const legacyQueryTemplateId =
    typeof component.properties.queryTemplateId === "string" &&
    component.properties.queryTemplateId.trim().length > 0
      ? component.properties.queryTemplateId.trim()
      : undefined;
  const binding = useMemo(
    () => parseComponentDataBinding(component.properties.dataBinding),
    [component.properties.dataBinding],
  );
  const hasComponentDataBindingProperty = Object.prototype.hasOwnProperty.call(
    component.properties,
    "dataBinding",
  );
  const schemaBindingSource = useMemo(() => {
    return binding?.source.kind === "schema" ? binding.source : null;
  }, [binding]);
  const queryTemplateViewSource = useMemo(() => {
    return binding?.source.kind === "query-template" ? binding.source : null;
  }, [binding]);
  const federatedViewSource = useMemo(() => {
    return binding?.source.kind === "federated-view" ? binding.source : null;
  }, [binding]);
  const schemaBindingWriteTarget = useMemo(() => {
    if (binding?.writeTarget === undefined) {
      return null;
    }

    return binding.writeTarget.kind === "federated-view-source"
      ? binding.writeTarget.source
      : binding.writeTarget;
  }, [binding]);
  const legacyDataSource = useMemo(
    () => createLegacyQueryTemplateDataSource(component),
    [component],
  );
  const legacyQueryTemplateEnabled =
    options.enabled !== false &&
    !hasComponentDataBindingProperty &&
    legacyDataSource !== undefined;
  const legacyQuery = useQueryTemplateData({
    dataSource: legacyDataSource,
    componentId: component.id,
    enabled: legacyQueryTemplateEnabled,
    templateId: legacyQueryTemplateId,
  });
  const schemaBindingQuery = useSchemaBindingData({
    source: schemaBindingSource,
    enabled: options.enabled !== false && schemaBindingSource !== null,
  });
  const dataViewRuntimeSource = queryTemplateViewSource ?? federatedViewSource;
  const dataViewRuntimeQuery = useComponentDataViewRuntime({
    view: dataViewRuntimeSource,
    enabled: options.enabled !== false && dataViewRuntimeSource !== null,
  });
  const schemaBindingWriteTargetResolution = useSchemaBindingSourceResolution({
    source: schemaBindingWriteTarget,
    operation: "update",
    enabled: options.enabled !== false && schemaBindingWriteTarget !== null,
  });
  const schemaBindingCreateTargetResolution = useSchemaBindingSourceResolution({
    source: schemaBindingWriteTarget,
    operation: "create",
    enabled: options.enabled !== false && schemaBindingWriteTarget !== null,
  });
  const schemaBindingDeleteTargetResolution = useSchemaBindingSourceResolution({
    source: schemaBindingWriteTarget,
    operation: "delete",
    enabled: options.enabled !== false && schemaBindingWriteTarget !== null,
  });

  const legacyQueryTemplateState =
    useMemo<ComponentDataResolverRuntimeState>(() => {
      const sourceError = legacyQuery.sourceErrorCode
        ? mapCatalogSourceErrorToResolverState(
            legacyQuery.sourceErrorCode,
            legacyQuery.error ?? "Query-template source requires reconnection.",
          )
        : null;

      return {
        rows: legacyQuery.data ?? undefined,
        loading: legacyQuery.isLoading,
        error: sourceError?.error ?? legacyQuery.error,
        reconnect: sourceError?.reconnect ?? null,
        refetch: legacyQuery.refetch,
      };
    }, [
      legacyQuery.data,
      legacyQuery.error,
      legacyQuery.isLoading,
      legacyQuery.refetch,
      legacyQuery.sourceErrorCode,
    ]);
  const schemaBindingState = useMemo<ComponentDataResolverRuntimeState>(() => {
    const sourceError = schemaBindingQuery.sourceErrorCode
      ? mapCatalogSourceErrorToResolverState(
          schemaBindingQuery.sourceErrorCode,
          schemaBindingQuery.error ??
            "Schema binding source requires reconnection.",
        )
      : null;

    return {
      rows: schemaBindingQuery.data ?? undefined,
      loading: schemaBindingQuery.isLoading,
      error: sourceError?.error ?? schemaBindingQuery.error,
      reconnect: sourceError?.reconnect ?? null,
      refetch: schemaBindingQuery.refetch,
    };
  }, [
    schemaBindingQuery.data,
    schemaBindingQuery.error,
    schemaBindingQuery.isLoading,
    schemaBindingQuery.refetch,
    schemaBindingQuery.sourceErrorCode,
  ]);
  const dataViewRuntimeState = useMemo<ComponentDataResolverRuntimeState>(() => {
    const sourceError = dataViewRuntimeQuery.sourceErrorCode
      ? mapCatalogSourceErrorToResolverState(
          dataViewRuntimeQuery.sourceErrorCode,
          dataViewRuntimeQuery.error ??
            "Component DataViewRef source requires reconnection.",
        )
      : null;

    return {
      rows: dataViewRuntimeQuery.data ?? undefined,
      loading: dataViewRuntimeQuery.isLoading,
      error: sourceError?.error ?? dataViewRuntimeQuery.error,
      reconnect: sourceError?.reconnect ?? null,
      preserveRowsOnSourceError: dataViewRuntimeQuery.hasPartialSourceError,
      sourceErrors: dataViewRuntimeQuery.sourceErrors,
      refetch: dataViewRuntimeQuery.refetch,
    };
  }, [
    dataViewRuntimeQuery.data,
    dataViewRuntimeQuery.error,
    dataViewRuntimeQuery.hasPartialSourceError,
    dataViewRuntimeQuery.isLoading,
    dataViewRuntimeQuery.refetch,
    dataViewRuntimeQuery.sourceErrorCode,
    dataViewRuntimeQuery.sourceErrors,
  ]);

  const bindingState =
    options.bindingState ??
    (schemaBindingSource !== null
      ? schemaBindingState
      : dataViewRuntimeSource !== null
        ? dataViewRuntimeState
        : undefined);
  const writeTargetState =
    useMemo<ComponentDataResolverWriteTargetRuntimeState>(() => {
      const operationResolutions = {
        create: schemaBindingCreateTargetResolution,
        update: schemaBindingWriteTargetResolution,
        delete: schemaBindingDeleteTargetResolution,
      } satisfies Record<
        ComponentDataWriteOperation,
        typeof schemaBindingWriteTargetResolution
      >;
      const failedResolution =
        operationResolutions.create.sourceErrorCode !== undefined
          ? operationResolutions.create
          : operationResolutions.update.sourceErrorCode !== undefined
            ? operationResolutions.update
            : operationResolutions.delete.sourceErrorCode !== undefined
              ? operationResolutions.delete
              : null;
      const sourceError = failedResolution?.sourceErrorCode
        ? mapCatalogSourceErrorToResolverState(
            failedResolution.sourceErrorCode,
            failedResolution.error ??
              "Write target source requires reconnection.",
          )
        : null;

      return {
        operations: {
          create: operationResolutions.create.writable,
          update: operationResolutions.update.writable,
          delete: operationResolutions.delete.writable,
        },
        requestingOrganizationId:
          operationResolutions.update.sourceResolution?.queryContext
            .requestingOrganizationId ??
          operationResolutions.create.sourceResolution?.queryContext
            .requestingOrganizationId ??
          operationResolutions.delete.sourceResolution?.queryContext
            .requestingOrganizationId ??
          null,
        loading:
          operationResolutions.create.isLoading ||
          operationResolutions.update.isLoading ||
          operationResolutions.delete.isLoading,
        error: sourceError?.error ?? failedResolution?.error ?? null,
        reconnect: sourceError?.reconnect ?? null,
      };
    }, [
      schemaBindingCreateTargetResolution,
      schemaBindingDeleteTargetResolution,
      schemaBindingWriteTargetResolution,
    ]);

  return useMemo(
    () =>
      resolveComponentData(component, {
        bindingState,
        writeTargetState:
          options.writeTargetState ??
          (schemaBindingWriteTarget !== null ? writeTargetState : undefined),
        legacyQueryTemplateState,
        refetch: options.refetch,
      }),
    [
      bindingState,
      component,
      legacyQueryTemplateState,
      options.refetch,
      options.writeTargetState,
      schemaBindingWriteTarget,
      writeTargetState,
    ],
  );
}
